/**
 * GENERATED from source/perception-action-coupling.md, verbatim. Do not edit the words here: edit the doc
 * (Coleman's) and regenerate, or the verbatim test fails. Structure (block
 * kinds, chapters, exercises, and each block's depth) is decided in
 * scripts/build-concepts.py; depth.js renders the Platform and
 * Certification versions from this one master lesson. See CLAUDE.md,
 * "Concept modules are verbatim", and .claude/skills/concept-module/SKILL.md.
 */

export const PERCEPTION_ACTION_COUPLING = {
  "key": "perception-action-coupling",
  "title": "Perception–Action Coupling",
  "category": "Concepts",
  "platform": {
    "key": "perception-action-coupling",
    "sub": "Why Doing a Skill Isn't the Same as Using It",
    "duration": "8 min read",
    "chapter": {
      "id": "perception-action-coupling",
      "title": "Perception–Action Coupling"
    }
  },
  "cert": {
    "key": "perception-action-coupling-cert",
    "duration": "14 min"
  },
  "chapters": [
    {
      "id": "see",
      "title": "You Still Have to See the Game",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "You Still Have to See the Game"
        },
        {
          "kind": "text",
          "md": "Let's start with something that should be obvious.\n\nSay a player decides before the possession starts that they're attacking left. No elaborate read. No waiting for the defender to tell them which move to use. They know what they're going to do.\n\nBut they still need to know **where** to do it. They need to manage the distance from their defender, the location of help, the space available, the timing of their first step, and their own balance and momentum. If the defender shifts six inches, if the gap closes early, or if their foot lands in a different position, the same planned attack may need to be executed differently.\n\n**Knowing what you're going to do doesn't mean you know exactly how you're going to do it.**\n\nThat's the practical idea behind **perception–action coupling**: athletes perceive information while acting, and their actions continually change the information available to them. Seeing and doing work together. We can separate aspects of them in training, but basketball rarely separates them for us.\n\nOne clarification matters. This isn't perception versus no perception. Even a shot with no defender requires information from the rim, ball, and body. The difference is **which information is available, how it changes, and what the athlete must do with it**."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Perception Is Much More Than Reacting to One Defender"
        },
        {
          "kind": "text",
          "md": "When coaches hear \"perception,\" they often picture a player reading the person directly in front of them. That's part of it, but basketball asks for much more:\n\n- **Defenders and help:** Positioning, momentum, angles, and emerging openings.\n- **Space and timing:** Where a move fits, how fast the window is closing, and how much room is available.\n- **Teammates:** Their movement, spacing, availability, and intentions.\n- **Game context:** Time, score, possession objectives, and changing priorities.\n- **The athlete's own body:** Balance, foot pressure, momentum, fatigue, rhythm, and the feel of the ball.\n\nSome of this information is anticipated rather than reacted to after the fact. Some is used without conscious analysis. A great player can know what they want to do and still be constantly adjusting how they do it."
        },
        {
          "kind": "bigIdea",
          "md": "A planned move is not a preprogrammed movement."
        }
      ]
    },
    {
      "id": "defense",
      "title": "Why the Same Skill Looks Different Against Defense",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Why the Same Skill Looks Different Against Defense"
        },
        {
          "kind": "text",
          "md": "Picture a shooter taking the same jumper over and over on-air. The ball comes in at a familiar time, the space is open, and much of the movement can settle into a comfortable rhythm.\n\nAdd a defender and the demands change. Now the shooter might have to release sooner, jump for longer, adjust their body position, or accept a catch and base that aren't quite what they'd choose in an empty gym.\n\nThat isn't necessarily a brand-new decision. It might be the same shot, **organized around different information**.\n\nThis is one reason live skill often looks less clean than workout skill. What we call \"imperfection\" is frequently a visible consequence of adapting the movement to what actually happened. That doesn't mean every awkward movement is functional or that misses are automatically productive. We aren't trying to make skills ugly. **We're trying to make them adaptable.**\n\nThere's an attention component, too. A player who must consciously think about every piece of a new movement has less attentional room to consciously process other parts of the game. And if they're working very hard to identify the right information, consciously controlling their technique may become more difficult. Skilled actions don't require zero attention, but greater automaticity can reduce the need to supervise every movement detail.\n\nImportantly, **attention is not the whole explanation**. Defender position and timing can directly require different actions, even from a player whose technique is highly automatic."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Attention Matters, But Doesn't Explain Everything",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Conscious technique control and deliberate information processing may compete for limited attention. A player focusing on every detail of a new dribble may struggle to track a defender's momentum at the same time. Greater automaticity can free some conscious attention.\n\nBut the opposite mistake is treating all movement changes under defense as a symptom of information overload. A defender who closes a gap earlier can make a quicker or differently organized movement necessary, even for an expert whose technique is automatic.\n\nAlso, perception isn't always a conscious analysis. Experienced athletes can use information from the defender, floor, and their own bodies without explicitly verbalizing it. **The purpose of coupling is not to think about everything; it's to become better at acting with the information that matters.**",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "research",
      "title": "The Research: A Defender Changes More Than the Make Percentage",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "The Research: A Defender Changes More Than the Make Percentage"
        },
        {
          "kind": "text",
          "md": "**Basketball shooting, 2016.** Gorman and Maloney studied 12 highly skilled junior male players taking several kinds of shots with and without a defender. Their average accuracy was **63.9% undefended versus 41.1% defended**. More interestingly, the presence of a defender also changed shot execution time, jump time, ball-flight time, and patterns of movement variability. The environment changed the movement, not just the result. [Read the study](https://doi.org/10.1016/j.psychsport.2016.08.003).\n\n**Basketball shooting, 2025.** Court Gold and colleagues replicated and extended that work with skilled female basketball players. The defended condition again led to faster shot execution, longer jump time, changes in ball flight, and increased variability in some timing measures. Their defended-shooting results were also strongly associated with competition shooting statistics. [Read the study](https://doi.org/10.1016/j.psychsport.2025.102828).\n\n**Perceptual training, 2024.** A review and meta-analysis of **22 team-sport studies** found that perceptual-cognitive training improved anticipation and decision-related measures, but gains on the trained laboratory tasks were larger than gains measured in sport settings. Learning to pick up information in one setting doesn't guarantee equivalent improvement in competition. [Read the review](https://doi.org/10.3390/bs14100919).\n\nThese findings support a useful coaching principle: **the information available during a repetition can change what an athlete actually practices.** They do not prove that every live drill is superior or that adding a defender automatically produces better long-term learning."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "A Closer Look at the Research",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "**Gorman and Maloney (2016):** Twelve skilled junior male basketball players performed several shot types defended and undefended. Accuracy decreased from 63.9% to 41.1%, while shot timing and movement patterns changed. This is direct evidence of **immediate movement adaptation**, not proof of long-term learning superiority.\n\n**Court Gold and colleagues (2025):** A replication and extension with skilled female basketball players also found faster execution, longer jumps, changes in ball flight, and greater variability in some measures under defense. Defended-test accuracy correlated strongly with the players' competition statistics. The correlation is informative, but does not establish that defended training caused better game shooting.\n\n**Pinder and colleagues (2011), cricket:** Batters responded differently to a live bowler, a ball projection machine, and a video simulation. Without the bowler's advance movement information, movement began later, peak bat speed was reduced, and contact worsened. The finding reinforces a broader point: **the available information can change how a movement is organized**.\n\n**2024 team-sport meta-analysis:** Across 22 studies, perceptual-cognitive training improved outcomes, but average effects were larger on trained laboratory tasks than on sport-transfer measures (response-accuracy effect sizes approximately 1.51 versus 0.65). That doesn't make video or isolated perceptual training worthless. It means improvement on a simplified assessment is not the same as equivalent improvement in competition.",
          "depth": "cert"
        },
        {
          "kind": "scienceNerd",
          "blocks": [
            {
              "kind": "prose",
              "md": "Perception and action are reciprocal. A player's movement changes their distance and angle to the defender, which changes the information available for the next movement. It's not necessarily a slow \"see, decide, act\" chain.\n\nThe studies above answer different questions. The basketball and cricket studies compared behavior under distinct immediate conditions; the meta-analysis evaluated training interventions and transfer. Keep that distinction in mind whenever someone claims that one type of practice is proven best. The pooled meta-analysis involved diverse interventions, so its averages are not precise predictions for individual athletes.\n\n**References:** [Gorman & Maloney (2016)](https://doi.org/10.1016/j.psychsport.2016.08.003) · [Court Gold et al. (2025)](https://doi.org/10.1016/j.psychsport.2025.102828) · [Pinder et al. (2011)](https://doi.org/10.3758/s13414-011-0102-1) · [2024 team-sport meta-analysis](https://doi.org/10.3390/bs14100919)"
            }
          ],
          "depth": "cert"
        }
      ]
    },
    {
      "id": "without",
      "title": "So Why Train Movements Without Defense?",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "So Why Train Movements Without Defense?"
        },
        {
          "kind": "text",
          "md": "Because the ability to execute a movement still matters.\n\nImagine a young player learning a between-the-legs dribble. They can barely execute it with no defender present. Now put them in a competitive 1v1. What happens? Often, they go right back to the crossover they already trust.\n\nThey might even beat their defender. But the target skill gets avoided, and the extra information and urgency can make learning that new coordination unnecessarily frustrating.\n\nSo spend some time building the movement itself. Let them discover the timing and make adjustments. Give them repetitions where **the difficulty is coming from the skill you actually want to improve**, not from a defender who makes it impossible to attempt.\n\nA useful sign here is the athlete who keeps getting close. They nearly catch the ball cleanly, nearly connect the bounce, and can feel what needs to change. That kind of reachable difficulty can make a repetitive task engaging. The point is that progress can be visible and the challenge can stay engaging without a defender. It is not a fixed formula for learning.\n\n**But capability is not the same thing as automaticity.**\n\nThe player doesn't need to master the between-the-legs before facing defense. Once they can access it often enough in a controlled setting to begin practicing it meaningfully, we can expose it to a defender. Maybe it works only some of the time. That's fine. There is no universal success-percentage gate.\n\nNow the player can begin organizing the movement around real distance, timing, and defensive information. Keep some targeted on-air work in the plan, and continue returning to live situations.\n\nThe same principle applies to a developing two-foot extension finish: build enough coordination to make the option available, then introduce variable, defender-driven conditions before waiting for a perfectly automatic version of the movement."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Capability Is a Threshold, Not a Finish Line",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "There's no universal percentage an athlete must achieve before adding a defender. Ask whether the target action is available enough that the athlete can begin learning to execute it under more variable demands.\n\nIf the movement is almost never accessible, brief targeted coordination work can help. Once the athlete can attempt it meaningfully, start reintroducing defender positioning, timing, and space. Continue on-air work where it adds value. Some athletes can develop the movement in a constrained live task from the start, so **this is a coaching decision, not a rigid developmental sequence**.\n\nIt helps to separate **capability** (I can access this movement) from **automaticity** (I usually execute it with little conscious technical supervision). We need some level of the former for targeted practice to work; we do not need the latter before adding live information.",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "build",
      "title": "Build the Movement. Then Teach It to Live in the Game.",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Build the Movement. Then Teach It to Live in the Game."
        },
        {
          "kind": "text",
          "md": "A useful practice rhythm looks like this:\n\n**1. Identify the limitation in play.** Is the athlete missing the opportunity, lacking the movement option, or struggling to coordinate the two?\n\n**2. Simplify what needs simplifying.** Sometimes that means no defense so the athlete can build action capacity. Sometimes it means keeping a defender but reducing the number of reads or people involved.\n\n**3. Reintroduce useful information early enough.** Once the movement is accessible at a workable level, allow the athlete to organize it around more game-like timing, space, and opposition.\n\n**4. Check what actually emerges.** Does the player access the target skill, or keep escaping into a familiar solution? Do they recognize when it makes sense to use it, not just when a rule demands it?\n\n**5. Move back and forth.** Live play reveals a limitation; targeted work develops it; live play tests it again. Learning rarely needs to follow a perfectly straight line."
        },
        {
          "kind": "bigIdea",
          "md": "You don't need to perfect a movement before introducing perception. You need enough capability to start learning how to use it with the game."
        },
        {
          "kind": "text",
          "md": "If we only train the action, we risk building a movement the athlete doesn't know how to use. If we only add game demands before the movement is accessible, the athlete may never get meaningful practice with the movement we intended to build.\n\n**Great development builds both the option and the ability to use it.**"
        },
        {
          "kind": "reflection",
          "md": "Think of one athlete whose workout skill doesn't consistently show up in games. **Is the real limitation that they can't perform the movement, that they don't recognize when to use it, or that they can't yet organize it around changing information?** What would you change in their next session to find out?",
          "depth": "platform"
        }
      ]
    },
    {
      "id": "deeper",
      "title": "One Movement, Different Problems",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "One Movement, Different Problems",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "When a player \"can't do\" something in a game, the explanation matters.\n\n**They can't organize the action yet.** The movement is unreliable even under relatively simple conditions. A defender may push them back toward a more familiar solution.\n\n**They can execute the movement, but don't recognize or time the opportunity.** Action capacity exists, but the player hasn't learned to coordinate it with the defender, space, and timing.\n\n**They can do each part separately, but the combination falls apart.** The player can execute the movement and can understand the read, yet performance breaks down when both must happen together. Attention, pressure, or the exact information-action relationship may be limiting factors.\n\nThe intervention depends on the problem. More 1v1 isn't automatically the answer. Neither are more isolated repetitions. **Did the athlete actually practice the target problem, or find another way to get the result?**",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "activity1",
      "title": "Certification Activity 1: What Is the Real Limiter?",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Certification Activity 1: What Is the Real Limiter?",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "**Scenario:** A young player is learning a between-the-legs dribble. In controlled repetitions the movement succeeds sometimes, but in live 1v1 they always revert to a crossover and rarely attempt the new action. They are still able to score.",
          "depth": "cert"
        },
        {
          "kind": "writtenResponse",
          "id": "limiter",
          "prompt": "**Write your response before opening the discussion:** What does their scoring tell you, and what does it not tell you? What two changes would you make to find out whether the limitation is action capacity, perception/timing, or over-reliance on the crossover?",
          "discussion": "Successful scoring tells you the player found a functional solution, not that they practiced your target. A brief on-air check can show whether the movement is accessible. Then you might keep live defense but simplify the starting picture or temporarily constrain away the habitual solution so the player can encounter the intended timing problem. Once the movement starts appearing, remove the artificial rule and see whether it remains an option when appropriate. There's no universal number of successful on-air repetitions that makes a player ready.",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "activity2",
      "title": "Certification Activity 2: Apply It to a Real Athlete",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Certification Activity 2: Apply It to a Real Athlete",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "athlete",
          "title": false,
          "numbered": true,
          "intro": "Choose an athlete you currently coach. Identify a movement they can perform better in isolated practice than in games.\n\n**Save three observations:**",
          "steps": [
            {
              "id": "controlled",
              "q": "What exactly can they do in controlled conditions? Be specific about the movement, not just the outcome.",
              "hint": "",
              "rows": 3
            },
            {
              "id": "demands",
              "q": "What information and other demands are present when the skill fails in play? Separate what you've observed from what you're guessing.",
              "hint": "",
              "rows": 3
            },
            {
              "id": "cycle",
              "q": "What brief cycle of action-capacity work and game-relevant exposure could help test the actual limitation?",
              "hint": "",
              "rows": 3
            }
          ],
          "discussion": "A good plan identifies a testable rate limiter, preserves enough information to practice the target, and checks whether the athlete can choose and adapt the skill when constraints are removed. A good plan also accepts that the first hypothesis might be wrong. The next session provides information, not only reps.",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "reflection",
      "title": "Final Reflection",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "Final Reflection",
          "depth": "cert"
        },
        {
          "kind": "writtenResponse",
          "id": "final",
          "prompt": "An athlete becomes far more consistent on-air but remains hesitant or ineffective against defenders. **At what point does adding more on-air repetition stop being the best answer, and what would you need to observe before deciding?**",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Research and BAM Reading",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "- Gorman, A. D., & Maloney, M. A. (2016). [Representative design: Does the addition of a defender change the execution of a basketball shot?](https://doi.org/10.1016/j.psychsport.2016.08.003). Psychology of Sport and Exercise, 27, 112–119. Acute defended versus undefended basketball comparison.\n- Court Gold, C. L., et al. (2025). [Sampling perception-action couplings from competition create representative basketball shooting tasks: A replication and extension of Gorman and Maloney (2016).](https://doi.org/10.1016/j.psychsport.2025.102828). Psychology of Sport and Exercise, 78, 102828. Acute basketball comparison plus competition correlation.\n- Pinder, R. A., Davids, K., Renshaw, I., & Araújo, D. (2011). [Manipulating informational constraints shapes movement reorganization in interceptive actions.](https://doi.org/10.3758/s13414-011-0102-1). Attention, Perception, & Psychophysics, 73, 1242–1254. Cricket information/movement comparison.\n- [Effects of Perceptual-Cognitive Training on Anticipation and Decision-Making Skills in Team Sports: A Systematic Review and Meta-Analysis](https://doi.org/10.3390/bs14100919). (2024). Behavioral Sciences, 14(10), 919. Twenty-two studies; training effects and limited transfer.\n- Coleman Ayers, The Modern Basketball Blueprint, Chapters 3–4, especially Representative Practice and Effective On-Air Training.\n- Coleman Ayers, Deep Skill, Chapter 11 (perception/action and transfer discussions) and Chapter 12 (Games Approach); Chapter 6 for Attentional Currency.",
          "depth": "cert"
        },
        {
          "kind": "relatedConcepts",
          "items": [
            {
              "key": "affordances",
              "title": "Affordances",
              "blurb": "What Does the Player Actually See?"
            },
            {
              "key": "representative-learning-design",
              "title": "Representative Learning Design",
              "blurb": "How Much of the Game Do We Really Need?"
            },
            {
              "key": "types-of-constraints",
              "title": "Types of Constraints",
              "blurb": "Design the Problem. Let the Athlete Solve It."
            },
            {
              "key": "attractors-metastability",
              "title": "Attractors, Multistability & Metastability",
              "blurb": "Stable Enough to Trust. Flexible Enough to Adapt."
            },
            {
              "key": "challenge-point",
              "title": "Challenge Point & Desirable Difficulties",
              "blurb": "Hard Is Not the Goal. Productive Struggle Is."
            },
            {
              "key": "degrees-of-freedom",
              "title": "Degrees of Freedom",
              "blurb": "Freedom Is Only Useful If You Can Organize It."
            },
            {
              "key": "repetition-without-repetition",
              "title": "Repetition Without Repetition",
              "blurb": "How to Get More Reps Without Just Reciting the Same Solution"
            },
            {
              "key": "attentional-currency",
              "title": "Attentional Currency",
              "soon": true
            }
          ]
        }
      ]
    }
  ]
};
